import type { AnalyticsEventType } from '@adarveo/shared';
import { supabase } from './supabase';

const storageKey = 'adarveo.visitor';
const sessionKey = 'adarveo.session';

const safeId = () => {
  if ('crypto' in window && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const getVisitorId = () => {
  const existing = localStorage.getItem(storageKey);
  if (existing) return existing;
  const created = safeId();
  localStorage.setItem(storageKey, created);
  return created;
};

export const getSessionId = () => {
  const existing = sessionStorage.getItem(sessionKey);
  if (existing) return existing;
  const created = safeId();
  sessionStorage.setItem(sessionKey, created);
  return created;
};

export const clientMetadata = () => {
  const search = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = search.get(key);
    if (value) utm[key] = value;
  }
  const ua = navigator.userAgent;
  const navigatorWithBrands = navigator as Navigator & {
    userAgentData?: { brands?: Array<{ brand: string; version: string }> };
  };
  const isMobile = /Mobi|Android/i.test(ua);
  return {
    device_type: isMobile ? 'mobile' : 'desktop',
    browser: navigatorWithBrands.userAgentData?.brands?.[0]?.brand ?? 'unknown',
    os: navigator.platform,
    referrer: document.referrer || '',
    ...utm
  };
};

export const trackEvent = async (
  publicSlug: string,
  eventType: AnalyticsEventType,
  metadata: Record<string, unknown> = {}
) => {
  if (!supabase) return { ok: false, skipped: true };
  const { data, error } = await supabase.functions.invoke('track-event', {
    body: {
      publicSlug,
      eventType,
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      metadata: { ...clientMetadata(), ...metadata }
    }
  });
  if (error) return { ok: false, error };
  return data;
};

export const trackCollectionEvent = async (
  publicSlug: string,
  eventType: AnalyticsEventType,
  metadata: Record<string, unknown> = {},
  targetIndex?: number
) => {
  if (!supabase) return { ok: false, skipped: true };
  const { data, error } = await supabase.functions.invoke('track-collection-event', {
    body: {
      publicSlug,
      eventType,
      targetIndex,
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      metadata: { ...clientMetadata(), ...metadata }
    }
  });
  if (error) return { ok: false, error };
  return data;
};
